import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Puzzle, Database, Brain } from "lucide-react";

const fade = {
  hidden: { opacity: 0, y: 28 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.55, ease: [0.25, 0.46, 0.45, 0.94] } }),
};

const points = [
  { icon: Puzzle, title: "Neutral Infrastructure", desc: "Integrates with existing EHRs and workflows without replacing them. Zero disruption.",
    gradient: "linear-gradient(135deg, #00A8B3 0%, #00838F 100%)" },
  { icon: Database, title: "Patient-Owned Data", desc: "Records belong to patients. Shared via consent, never stored by intermediaries.",
    gradient: "linear-gradient(135deg, #28A745 0%, #1B8A36 100%)" },
  { icon: Brain, title: "Non-Diagnostic AI", desc: "AI assists with summaries and context only. Never diagnoses. Trust-safe by design.",
    gradient: "linear-gradient(135deg, #0097A7 0%, #006064 100%)" },
];

const scrollTo = (e, href) => { e.preventDefault(); document.querySelector(href)?.scrollIntoView({ behavior: "smooth" }); };

export const DifferenceSection = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section id="difference" className="py-20 lg:py-28" style={{ background: '#E8EFF5' }} ref={ref}>
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <motion.div variants={fade} initial="hidden" animate={inView ? "visible" : "hidden"} className="text-center mb-12">
          <span className="text-spaced mb-3 block">W H Y &nbsp; C A R E C O N T E X T</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold mb-4" style={{ color: '#1A1F36' }}>The CareContext Difference</h2>
          <p className="text-base max-w-2xl mx-auto" style={{ color: '#6B7280' }}>
            Neutral infras with patient owned data and strictly assistive (non-diagnostic) AI built for authenticity, transparency, and lasting trust in healthcare.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
          {points.map((p, i) => (
            <motion.div key={p.title}
              initial={{ opacity: 0, y: 40, scale: 0.9 }}
              animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
              transition={{ delay: i * 0.2 + 0.3, type: "spring" }}
              whileHover={{ y: -6, scale: 1.03 }}
            >
              <div className="rounded-2xl p-7 h-full flex flex-col text-center text-white shadow-lg relative overflow-hidden"
                style={{ background: p.gradient }}>
                {/* Shimmer */}
                <motion.div className="absolute inset-0 pointer-events-none"
                  style={{ background: 'linear-gradient(110deg, transparent 30%, rgba(255,255,255,0.07) 50%, transparent 70%)' }}
                  animate={{ x: ['-100%', '200%'] }}
                  transition={{ duration: 4, repeat: Infinity, delay: i * 0.6 + 1 }} />
                {/* Decorative circle */}
                <div className="absolute -top-8 -right-8 w-28 h-28 rounded-full" style={{ background: 'rgba(255,255,255,0.06)' }} />

                <motion.div
                  whileHover={{ scale: 1.15, rotate: 10 }}
                  className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-5 relative z-10"
                  style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)' }}>
                  <p.icon className="w-7 h-7 text-white" />
                </motion.div>
                <h4 className="text-base font-heading font-bold mb-2 relative z-10">{p.title}</h4>
                <p className="text-sm leading-relaxed relative z-10" style={{ color: 'rgba(255,255,255,0.8)' }}>{p.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div variants={fade} initial="hidden" animate={inView ? "visible" : "hidden"} custom={4} className="text-center">
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Button size="lg" className="btn-teal px-8" onClick={(e) => scrollTo(e, "#demo")}>Book a Demo</Button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
